import { useEffect, useState, useRef } from "react"; 
import { collection, getDocs, query, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { db } from "services/firebase";
import { useNotifications } from "context/NotificationContext";
import { MdSmartphone, MdBarChart, MdHome, MdPerson, MdTablet } from "react-icons/md";
import { useAuth } from "./AuthContext";
import { getFunctions, httpsCallable } from "firebase/functions";
import notificationSound from "assets/sounds/notification-bell.mp3";

interface AppNotification {
  id: string;
  title: string;
  description: string;
  icon: JSX.Element;
  route: string;
}

function todayLocalYmd(): string {
  const now = new Date();
  return now.toISOString().split("T")[0];
}

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const useNotificationHandler = () => {
  const { user } = useAuth();
  const { notifications, resetSingleNotification, resetAllNotifications } = useNotifications();
  const [visibleNotifications, setVisibleNotifications] = useState<AppNotification[]>([]);
  const prevNotifications = usePrevious(notifications);
  const clientePath = user ? `clientes/${user.uid}` : null;
  const audioPlayer = useRef(new Audio(notificationSound));

  useEffect(() => {
    if (!clientePath) return;
    const q = query(collection(db, `${clientePath}/solicitacoes`));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const hojeStr = todayLocalYmd();
      const encontrou = snapshot.docs.some(d => d.data().Data === hojeStr && d.data().Status === "Confirmar");
      if (encontrou) {
        const switchDocRef = doc(db, `${clientePath}/notifications/switch1`);
        updateDoc(switchDocRef, { notified: true }).catch(err => console.error("Erro ao notificar switch1:", err));
      }
    });
    return () => unsubscribe();
  }, [clientePath]);

  useEffect(() => {
    if (!clientePath) return;
    const q = query(collection(db, `${clientePath}/relatorios`));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const hojeStr = todayLocalYmd();
      const encontrou = snapshot.docs.some(d => d.data().Data === hojeStr);
      if (encontrou) {
        const switchDocRef = doc(db, `${clientePath}/notifications/switch3`);
        updateDoc(switchDocRef, { notified: true }).catch(err => console.error("Erro ao notificar switch3:", err));
      }
    });
    return () => unsubscribe();
  }, [clientePath]);

  useEffect(() => {
    if (!clientePath) return;
    const q = query(collection(db, `${clientePath}/assinatura`));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const encontrou = snapshot.docs.some(d => d.data().Status === "Vencida");
      if (encontrou) {
        const switchDocRef = doc(db, `${clientePath}/notifications/switch8`);
        updateDoc(switchDocRef, { notified: true }).catch(err => console.error("Erro ao notificar switch8:", err));
      }
    });
    return () => unsubscribe();
  }, [clientePath]);

  useEffect(() => {
    if (!clientePath) return;
    const q = query(collection(db, `${clientePath}/unidades`));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const encontrou = snapshot.docs.some(d => d.data().sobraLimpaExecutado === false);
      if (encontrou) {
        const switchDocRef = doc(db, `${clientePath}/notifications/switch11`);
        updateDoc(switchDocRef, { notified: true }).catch(err => console.error("Erro ao notificar switch11:", err));
      }
    });
    return () => unsubscribe();
  }, [clientePath]);

  useEffect(() => {
    if (!clientePath) {
      setVisibleNotifications([]);
      return;
    }

    const newVisibleNotifications: AppNotification[] = [];
    const functions = getFunctions(undefined, "us-central1");
    const sendSMSCallable = httpsCallable(functions, "sendSMS" );
    let numeroSMS: string | null = null;
    let hasNewNotification = false;

    const getSmsNumber = async () => {
        if (numeroSMS) return numeroSMS;
        const assinaturaSnap = await getDocs(query(collection(db, `${clientePath}/assinatura`)));
        if (!assinaturaSnap.empty) {
            const d = assinaturaSnap.docs[0].data();
            if (d?.Numero) numeroSMS = d.Numero;
        }
        return numeroSMS;
    }

    const processNotifications = async () => {
        for (const currentConfig of notifications) {
          const prevConfig = prevNotifications?.find(p => p.id === currentConfig.id);
          let appNotif: AppNotification | null = null;

          if (currentConfig.checked && currentConfig.notified) {
            if (currentConfig.id === 'switch1') {
                appNotif = { id: 'switch1', title: "Aplicativo:", description: "Chegaram novas solicitações dos dispositivos.", icon: <MdSmartphone />, route: "/admin/dispositivos" };
            } else if (currentConfig.id === 'switch3') {
                appNotif = { id: 'switch3', title: "Dashboard:", description: "Existem relatórios disponíveis para Download.", icon: <MdBarChart />, route: "/admin/relatorios" };
            } else if (currentConfig.id === 'switch6') {
                appNotif = { id: 'switch6', title: "Dashboard:", description: "Um dispositivo está com erro, verifique o status.", icon: <MdHome />, route: "/admin/unidades" };
            } else if (currentConfig.id === 'switch8') {
                appNotif = { id: 'switch8', title: "Dashboard:", description: "Sua fatura venceu, acesse o Perfil para renovar.", icon: <MdPerson />, route: "/admin/perfil" };
            } 
            else if (currentConfig.id === 'switch11') {
                let nomeUnidade = "Uma unidade";
                
                const unidadesSnap = await getDocs(query(collection(db, `${clientePath}/unidades`)));
                const unidadePendente = unidadesSnap.docs.find(d => d.data().sobraLimpaExecutado === false);
                
                if (unidadePendente && unidadePendente.data().nome) {
                    nomeUnidade = unidadePendente.data().nome;
                }

                appNotif = { 
                    id: 'switch11', 
                    title: "Aplicativo:", 
                    description: `A unidade ${nomeUnidade} iniciou um novo expediente, finalize a Sobra Limpa do anterior.`, 
                    icon: <MdTablet />, 
                    route: "/admin/unidades"
                };
            }

            if (appNotif) {
              newVisibleNotifications.push(appNotif);
            }
          }

          const isNewNotification = 
            currentConfig.notified &&
            prevConfig?.notified === false;

          if (isNewNotification) {
            if (currentConfig.id !== 'switch10') { 
                hasNewNotification = true;
            }

            const smsSwitchId = `switch${parseInt(currentConfig.id.replace('switch', '')) + 1}`;
            const smsConfig = notifications.find(n => n.id === smsSwitchId);

            if (smsConfig?.checked) {
              const number = await getSmsNumber();
              if (number && appNotif) {
                  sendSMSCallable({ to: number, message: `🔔 ${appNotif.title}\n${appNotif.description}` })
                    .catch(err => console.error("Erro ao enviar SMS:", err));
              }
            }
          }
        }
        setVisibleNotifications(newVisibleNotifications);

        const soundSwitch = notifications.find(n => n.id === 'switch10');
        if (hasNewNotification && soundSwitch?.checked) {
            audioPlayer.current.play().catch(error => {
                console.log("A reprodução de áudio foi bloqueada pelo navegador até a primeira interação do usuário.", error);
            });
        }
    }

    if (notifications && prevNotifications) {
      processNotifications();
    }

  }, [notifications, clientePath, prevNotifications]);

  const dismissNotification = (id: string) => {
    resetSingleNotification(id);
  };
  
  const dismissAllNotifications = () => {
    resetAllNotifications();
  };

  return { visibleNotifications, dismissNotification, dismissAllNotifications };
};